import { createSheetStore } from '@bridge/ui';
import { z } from 'zod';

import env from 'lib/env';

import type { BaseSheetProps, SheetProps as FactorySheetProps } from '@bridge/ui';
import type { WebhookResponse } from 'types/api/webhooks';
import type { DashboardTask, DashboardTaskType } from 'types/domain/dashboard-task';
import type { PaymentProduct, PaymentReference, PaymentRoute, PaymentsHistoryEvent } from 'types/domain/payments';
import type { Wallet } from 'types/domain/wallets';

export type { BaseSheetProps };

// Task sheets are deep-linked from the home page by task type only (the sheet fetches the rest).
const taskSheetUrlProps = z.strictObject({ taskType: z.string() });

function taskSheetEntry<TComponent extends () => Promise<unknown>>(component: TComponent) {
  return { component, urlOpenable: taskSheetUrlProps };
}

// Sheet registry with dynamic imports. `urlOpenable` detail sheets carry only scalar ids (the
// sheet re-fetches the record); CreateRoute is pinned to its safe prefill shape — explicitly NOT
// the destination/address fields that `CreateRouteStoreData` otherwise allows.
const sheetRegistry = {
  CreateRoute: {
    component: () => import('../app/app/payments/_components/CreateRoute/CreateRouteSheet'),
    urlOpenable: z.strictObject({
      initialState: z.strictObject({
        routeType: z.enum(['onramp', 'offramp', 'c2c']),
        data: z.strictObject({ customerId: z.string().optional() }).optional(),
      }),
    }),
  },
  PaymentDetails: {
    component: () => import('../app/app/payments/_components/PaymentDetails/PaymentDetailsSheet'),
    urlOpenable: z.strictObject({ paymentEventId: z.string() }),
  },
  PaymentRoute: {
    component: () => import('../app/app/payments/_components/PaymentRouteDetails/PaymentRouteSheet'),
    urlOpenable: z.strictObject({
      paymentRouteId: z.string(),
      paymentRouteType: z.string(),
      customerId: z.string(),
    }),
  },
  CardDetails: () => import('../app/app/cards/_components/CardDetails/CardDetailsSheet'),
  CardTransactionDetails: () =>
    import('../app/app/cards/_components/CardTransactionDetails/CardTransactionDetailsSheet'),
  WalletDetails: () => import('../app/app/wallets/_components/WalletDetails/WalletDetailsSheet'),
  UnmatchedDepositMismatchedMemoSheet: taskSheetEntry(
    () => import('../app/app/home/_components/tasks/UnmatchedDepositMismatchedMemoSheet')
  ),
  UnmatchedDepositMismatchedAmountSheet: taskSheetEntry(
    () => import('../app/app/home/_components/tasks/UnmatchedDepositMismatchedAmountSheet')
  ),
  MissingCryptoReturnPolicySheet: taskSheetEntry(
    () => import('../app/app/home/_components/tasks/MissingCryptoReturnPolicySheet')
  ),
  WebhookDeliveryFailureSheet: taskSheetEntry(
    () => import('../app/app/home/_components/tasks/WebhookDeliveryFailureSheet')
  ),
  PausedUsdtTradesSheet: taskSheetEntry(() => import('../app/app/home/_components/tasks/PausedUsdtTradesSheet')),
  DeactivatedFeeAccountSheet: taskSheetEntry(
    () => import('../app/app/home/_components/tasks/DeactivatedFeeAccountSheet')
  ),
  MissingFeeAccountSheet: taskSheetEntry(() => import('../app/app/home/_components/tasks/MissingFeeAccountSheet')),
  CustomerUpliftRequiredSheet: taskSheetEntry(
    () => import('../app/app/home/_components/tasks/CustomerUpliftRequiredSheet')
  ),
  WebhookDetails: () => import('../app/app/settings/product/webhooks/_components/WebhookDetailsSheet'),
  ApprovalTaskSheet: {
    component: () => import('../app/app/home/_components/tasks/approval/ApprovalTaskSheet'),
    urlOpenable: z.strictObject({ taskId: z.string() }),
  },
  SensitiveActionDetails: {
    component: () => import('../app/app/home/_components/tasks/approval/SensitiveActionDetailsSheet'),
    urlOpenable: z.strictObject({ sensitiveActionId: z.string() }),
  },
  AboutFraudReporting: () => import('../app/app/reporting/_components/AboutFraudReportingSheet'),
  ApiKeyScopesSheet: () => import('../app/app/keys/_components/ApiKeyScopesSheet'),
  GenerateKeySheet: () => import('../app/app/keys/_components/GenerateKeySheet'),
  IpConfigurationSheet: () => import('../app/app/keys/_components/IpConfigurationSheet'),
  DevFeatureFlags: () => import('../components/dev/DevFeatureFlagsSheet'),
} as const;

// Per-sheet prop overrides — narrower public surface than what each component's full prop type
// expects. Most callers pass an ID + the renderer's container fetches the full record, so the
// override accepts either the eagerly-resolved object or just the identifier.
type SheetPropsOverrides = {
  PaymentDetails:
    { paymentEvent: PaymentsHistoryEvent } | { paymentEventId: string } | { paymentReference: PaymentReference };
  PaymentRoute:
    { paymentRoute: PaymentRoute } | { paymentRouteId: string; paymentRouteType: PaymentProduct; customerId: string };
  WebhookDetails: { webhook: WebhookResponse } | { webhookId: string };
  WalletDetails: { wallet: Wallet } | { walletId: string; customerId: string };
  UnmatchedDepositMismatchedMemoSheet: { task: DashboardTask } | { taskType: DashboardTaskType };
  UnmatchedDepositMismatchedAmountSheet: { task: DashboardTask } | { taskType: DashboardTaskType };
  MissingCryptoReturnPolicySheet: { task: DashboardTask } | { taskType: DashboardTaskType };
  WebhookDeliveryFailureSheet: { task: DashboardTask } | { taskType: DashboardTaskType };
  PausedUsdtTradesSheet: { task: DashboardTask } | { taskType: DashboardTaskType };
  DeactivatedFeeAccountSheet: { task: DashboardTask } | { taskType: DashboardTaskType };
  MissingFeeAccountSheet: { task: DashboardTask } | { taskType: DashboardTaskType };
  CustomerUpliftRequiredSheet: { task: DashboardTask } | { taskType: DashboardTaskType };
  ApprovalTaskSheet: { task: DashboardTask } | { taskId: string };
  SensitiveActionDetails: { sensitiveActionId: string };
  AboutFraudReporting: {
    rollupWindowDays: number | null;
    thresholdBps: number | null;
    windowDays: number | null;
  };
  ApiKeyScopesSheet: { scopes: string[] };
  GenerateKeySheet: { isSandbox: boolean; hasMaxUnrestrictedKeys: boolean };
  IpConfigurationSheet: { apiKeyId: string; apiKeyLabel: string };
};

export type SheetIdentifier = keyof typeof sheetRegistry;

export type SheetProps<ID extends SheetIdentifier> = FactorySheetProps<typeof sheetRegistry, ID, SheetPropsOverrides>;

export const {
  useSheetStore,
  useSheet,
  preloadSheets,
  SheetRenderer: BaseSheetRenderer,
  getSheetUrl,
} = createSheetStore<typeof sheetRegistry, SheetPropsOverrides>(sheetRegistry, {
  baseUrl: env.DOMAIN,
  // Dev-only sheets whose chunks should not ship in production bundles.
  excludeFromPreload: ['DevFeatureFlags'],
});

export { sheetRegistry };
